import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button, SegmentedTabs } from "@akuna/ui";
import { Plus } from "lucide-react";
import { useDaycareHotelAccess } from "@/hooks/useDaycareHotelAccess";
import PanelTab from "./tabs/PanelTab";
import ScheduleTab from "./tabs/ScheduleTab";
import ActivitiesTab from "./tabs/ActivitiesTab";
import AccommodationsTab from "./tabs/AccommodationsTab";
import SettingsTab from "./settings/SettingsTab";
import NewReservationDialog from "./reservation/NewReservationDialog";
import { useStayShowFilter } from "./useStayShowFilter";

const BASE = "/daycare-hotel";
const TABS = [
  { seg: "", labelKey: "tabPanel", Comp: PanelTab },
  { seg: "schedule", labelKey: "tabSchedule", Comp: ScheduleTab },
  { seg: "activities", labelKey: "tabActivities", Comp: ActivitiesTab },
  { seg: "accommodations", labelKey: "tabAccommodations", Comp: AccommodationsTab },
  { seg: "settings", labelKey: "tabSettings", Comp: SettingsTab },
] as const;

export default function DaycareHotelPage() {
  const { t } = useTranslation("daycareHotel");
  const { t: tNav } = useTranslation("nav");
  const location = useLocation();
  const navigate = useNavigate();
  const { labelKey } = useDaycareHotelAccess();
  const { show, setShow, canFilter } = useStayShowFilter();
  const [reservationOpen, setReservationOpen] = useState(false);
  useEffect(() => {
    if (location.pathname === `${BASE}/plans`) navigate("/reports/daycare-hotel", { replace: true });
  }, [location.pathname, navigate]);
  const activeSeg = useMemo(() => { const rest = location.pathname.replace(BASE, "").replace(/^\//, ""); return TABS.some((tab) => tab.seg === rest) ? rest : ""; }, [location.pathname]);
  const activeTab = TABS.find((tab) => tab.seg === activeSeg) ?? TABS[0];
  return <div className="mx-auto w-full max-w-7xl py-6">
    <header className="mb-4 flex flex-col items-start gap-3 sm:flex-row sm:items-center sm:justify-between"><h1 className="text-2xl font-extrabold tracking-tight text-foreground">{tNav(labelKey)}</h1>
      {canFilter && <div className="flex items-center gap-2"><span className="text-sm text-muted-foreground">{t("filterLabel")}</span><SegmentedTabs variant="outline-black" value={show} onValueChange={setShow} items={[{ value: "all", label: t("filterAll") }, { value: "daycare", label: t("filterDaycare") }, { value: "hotel", label: t("filterHotel") }]} /></div>}
    </header>
    <div className="w-full">
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <SegmentedTabs
          variant="outline"
          width="equal"
          value={activeSeg}
          onValueChange={(seg) => navigate(seg ? `${BASE}/${seg}` : BASE)}
          items={TABS.map((tab) => ({ value: tab.seg, label: t(tab.labelKey) }))}
        />
        <Button className="w-full sm:w-auto" onClick={() => setReservationOpen(true)}><Plus className="mr-1.5 h-4 w-4" />{t("newReservation")}</Button>
      </div>
      <activeTab.Comp show={show} />
    </div>
    <NewReservationDialog open={reservationOpen} onOpenChange={setReservationOpen} />
  </div>;
}
